import { clamp, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { filter, fromEvent, map, switchMap, takeUntil, tap } from 'rxjs';

import { useSubscriptions, useWatch } from '@/hook';
import { setStyleProperties, withTarget } from '@/util';

import { TRACK_TYPE, type VBaseTrackType } from '../VBaseTrack';
import { SLIDER_TRACK_MARKER_GAP, SLIDER_TRACK_MARKER_SIZE } from './constant';
import { VSliderContext } from './context';
import type { VSliderContextValue } from './type';
import {
  createSliderValueUpdater,
  getClosestMarkerItem,
  getLeft,
  getSafeValue,
  getTrackMarkerLeft,
} from './util';

export const useVSliderContext = <T extends VBaseTrackType>() =>
  VSliderContext.hook<VSliderContextValue<T>>();

export const useVSliderThumb = <T extends VBaseTrackType>({
  disabled,
  normalizedMarkerItemMap,
  normalizedValue,
  max,
  min,
  readOnly,
  step,
  thumbValue,
  type,
  value,
  setValue,

  onThumbMove,
}: Pick<
  VSliderContextValue<T>,
  | 'disabled'
  | 'normalizedMarkerItemMap'
  | 'normalizedValue'
  | 'max'
  | 'min'
  | 'readOnly'
  | 'step'
  | 'type'
  | 'value'
  | 'setValue'
> & {
  thumbValue: number;
  onThumbMove?(
    payload: {
      reversed?: boolean;
      thumbTx: number;
      thumbValue: number;
    },
    trigger: any,
  ): void;
}) => {
  const rangeValueSetList = useMemo(
    () => normalizedValue.map(v => new Set(v)),
    [normalizedValue],
  );
  const $thumb = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const thumbRangeIndex = rangeValueSetList.findIndex(set =>
    set.has(thumbValue),
  );
  const nonHiddenMax = normalizedMarkerItemMap
    ? Object.values(normalizedMarkerItemMap).reduce(
        (acc, v) => (v.hidden ? acc : v.value >= acc ? v.value : acc),
        -1,
      )
    : max;
  const thumbRangeValue =
    type === TRACK_TYPE.NORMAL
      ? [min, normalizedValue[thumbRangeIndex][0]]
      : normalizedValue[thumbRangeIndex];
  const maxThumbRangeValueLeft = getTrackMarkerLeft({
    min,
    step,
    value: thumbRangeValue[1],
  });
  const minThumbRangeValueLeft = getTrackMarkerLeft({
    min,
    step,
    value: thumbRangeValue[0],
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const thumbRangeValueTx = [
    minThumbRangeValueLeft - SLIDER_TRACK_MARKER_SIZE,
    maxThumbRangeValueLeft - SLIDER_TRACK_MARKER_SIZE,
  ];
  const isStartThumb =
    type === TRACK_TYPE.NORMAL ? false : thumbRangeValue[0] === thumbValue;

  const thumbThresholdValue = [
    normalizedValue[thumbRangeIndex - 1]?.[1]
      ? normalizedValue[thumbRangeIndex - 1][1] + step
      : min,
    normalizedValue[thumbRangeIndex + 1]?.[0]
      ? normalizedValue[thumbRangeIndex + 1][0] - step
      : nonHiddenMax,
  ];
  const maxThumbThresholdValueLeft = getTrackMarkerLeft({
    min,
    step,
    value: thumbThresholdValue[1],
  });
  const minThumbThresholdValueLeft = getTrackMarkerLeft({
    min,
    step,
    value: thumbThresholdValue[0],
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const thumbThresholdTx = [
    minThumbThresholdValueLeft - SLIDER_TRACK_MARKER_SIZE,
    maxThumbThresholdValueLeft - SLIDER_TRACK_MARKER_SIZE,
  ];

  const isStartThumbRef = useRef(isStartThumb);
  const normalizedMarkerItemMapRef = useRef(normalizedMarkerItemMap);
  const normalizedValueRef = useRef(normalizedValue);
  const thumbRangeValueTxRef = useRef(thumbRangeValueTx);
  const thumbThresholdTxRef = useRef(thumbThresholdTx);
  const thumbValueRef = useRef(thumbValue);

  const updateThumbTx = () => {
    if (!$thumb.current) return;

    const safeThumbValue = getSafeValue({
      max: nonHiddenMax,
      step,
      value: thumbValue,
    });
    const findedTrackLeft = getLeft({
      gap: SLIDER_TRACK_MARKER_GAP,
      min,
      size: SLIDER_TRACK_MARKER_SIZE,
      step,
      value: safeThumbValue,
    });

    const tx = findedTrackLeft - SLIDER_TRACK_MARKER_SIZE;

    setStyleProperties($thumb.current, { '--tx': `${tx}px` });

    if (!initializedRef.current) return;

    onThumbMove?.({ thumbTx: tx, thumbValue }, 'changeThumbValue');
  };

  useEffect(() => {
    isStartThumbRef.current = isStartThumb;
    normalizedMarkerItemMapRef.current = normalizedMarkerItemMap;
    normalizedValueRef.current = normalizedValue;
    thumbRangeValueTxRef.current = thumbRangeValueTx;
    thumbThresholdTxRef.current = thumbThresholdTx;
    thumbValueRef.current = thumbValue;
  }, [
    isStartThumb,
    normalizedMarkerItemMap,
    normalizedValue,
    thumbRangeValueTx,
    thumbThresholdTx,
    thumbValue,
  ]);

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>($thumb.current!, 'mousedown')
        .pipe(
          withTarget,
          filter(
            () =>
              !disabled && !readOnly && !!normalizedMarkerItemMapRef.current,
          ),
          tap(e => e.stopPropagation()),
          switchMap(e => {
            const target = $thumb.current!;
            const prevTx = +target.style
              .getPropertyValue('--tx')
              ?.replace('px', '');
            const sx = e.clientX;
            const [minLeft, maxLeft] = thumbThresholdTxRef.current;
            let ex = sx;
            let tx = prevTx;

            target.ariaPressed = 'true';

            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              map(e => e.clientX),
              map(mx => mx - sx),
              filter(diffX => Math.abs(diffX) > 0),
              tap(diffX => {
                let reversed = false;

                tx = clamp(diffX + prevTx, minLeft, maxLeft);

                setStyleProperties(target, { '--tx': `${tx}px` });
                ex = diffX + prevTx;

                if (isStartThumbRef.current) {
                  reversed = tx > thumbRangeValueTxRef.current[1];
                } else {
                  reversed = tx < thumbRangeValueTxRef.current[0];
                }

                onThumbMove?.(
                  { reversed, thumbTx: tx, thumbValue: thumbValueRef.current },
                  $thumb.current,
                );
              }),
              takeUntil(
                fromEvent(window, 'mouseup').pipe(
                  filter(
                    () => ex != sx && !!normalizedMarkerItemMapRef.current,
                  ),
                  tap(() => {
                    const closestMarkerItem = getClosestMarkerItem({
                      markerItemMap: normalizedMarkerItemMapRef.current!,
                      x: tx,
                    });

                    if (closestMarkerItem.value !== thumbValueRef.current) {
                      const changeValue = createSliderValueUpdater({
                        normalizedValue: normalizedValueRef.current!,
                        min,
                        type,
                        setValue: newValue => setValue(newValue, 'thumb'),
                      });

                      changeValue(
                        closestMarkerItem.value,
                        thumbValueRef.current,
                      );
                    } else {
                      updateThumbTx();
                    }

                    target.ariaPressed = null;
                  }),
                ),
              ),
            );
          }),
        )
        .subscribe(),
    ],
    [disabled, min, readOnly, type],
  );

  useWatch(
    [normalizedMarkerItemMap, thumbValue, value] as const,
    () => {
      updateThumbTx();
      initializedRef.current = true;
    },
    {
      immediate: true,
      equalityFn(
        [newNormalizedMarkerItemMap, newThumbValue, newValue],
        [oldNormalizedMarkerItemMap, oldThumbValue, oldValue],
      ) {
        return (
          isEqual(newNormalizedMarkerItemMap, oldNormalizedMarkerItemMap) &&
          newThumbValue === oldThumbValue &&
          isEqual(newValue, oldValue)
        );
      },
    },
  );

  return { $thumb, thumbThresholdTx };
};
